import type { MutableRefObject } from 'react';
import { createTokenCredential, type ProviderAccountCreateInput } from '@/api/endpoints';
import type { ProviderAccount } from '@/api/schemas';
import { nextAttempt, type Attempt } from './credentialModel';
import { CONNECT_COPY, connectBody, type ConnectValues, type StoredToken } from './providerAccountModel';

interface ConnectDeps {
  values: ConnectValues;
  /** The token an earlier attempt already stored: attached instead of stored twice. */
  reuse: StoredToken | null;
  attempt: MutableRefObject<Attempt | null>;
  create: (v: { body: ProviderAccountCreateInput; idempotencyKey: string }) => Promise<ProviderAccount>;
  /** The token was stored: the form keeps its id (never the token) and clears the field. */
  onStored: (token: StoredToken) => void;
}

/** One attempt, one key: the button is busy while it runs, and a failed attempt never stored anything. */
async function storeToken(values: ConnectValues, onStored: ConnectDeps['onStored']): Promise<string> {
  const credential = await createTokenCredential(CONNECT_COPY[values.kind].credentialKind, `${values.name.trim()} token`, values.token.trim(), crypto.randomUUID());
  onStored({ id: credential.id, kind: values.kind });
  return credential.id;
}

/** Stores the token once (`POST /credentials`), then creates the account that references it. */
export async function connectProviderAccount({ values, reuse, attempt, create, onStored }: ConnectDeps): Promise<ProviderAccount> {
  const credentialId = reuse?.id || (await storeToken(values, onStored));
  const body = connectBody(values, credentialId);
  attempt.current = nextAttempt(attempt.current, body);
  return create({ body, idempotencyKey: attempt.current.key });
}
